import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowsClockwise, MagnifyingGlass, WarningCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import OrderViewDialog from "@/components/OrderViewDialog";
import CustomerLink from "@/components/CustomerLink";
import ActivityAvatar, { ActivityColumnHeader } from "@/components/ActivityAvatar";
import { getLastActivity } from "@/lib/orders/activity";
import RackPriceChangeCard from "@/components/RackPriceChangeCard";

/**
 * Orders workspace — the single location for viewing current AND historical
 * orders. PO directive 2026-02-04: rename "Today's Orders" → "Orders" and
 * add a Period selector beneath the title so all office users can answer
 * questions about current and recent orders from one consistent workspace.
 *
 * "Previous Period" uses the DISC-001 Operational Period model, so Monday's
 * previous period is Fri–Sun, not literally "yesterday".
 */
const PERIOD_OPTIONS = [
  { key: "today",    label: "Today" },
  { key: "previous", label: "Previous Period" },
  { key: "last7",    label: "Last 7 Days" },
  { key: "month",    label: "This Month" },
  { key: "custom",   label: "Custom Range…" },
];

function _startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function _addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function _fmtDate(d, opts) { return d.toLocaleDateString("en-CA", opts); }

// Local-calendar YYYY-MM-DD builder. Never uses toISOString(), which
// would render UTC and drift a Toronto operator's business date at
// the evening/morning UTC rollover. (v1.2.2 · Reconciliation fix pattern.)
function _toBusinessDate(d) {
  const y  = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

// DISC-001 · Previous Operational Period. Weekday-based (statutory
// holidays are a future enhancement — flagged in the acceptance notes).
function previousOperationalPeriod(reference = new Date()) {
  const today = _startOfDay(reference);
  const dow = today.getDay();  // 0 Sun … 6 Sat
  if (dow === 1) return { start: _addDays(today, -3), end: today };            // Monday → Fri–Sun
  if (dow === 0) return { start: _addDays(today, -2), end: _addDays(today, -1) }; // Sunday → Friday only
  if (dow === 6) return { start: _addDays(today, -1), end: today };            // Saturday → Friday only
  return { start: _addDays(today, -1), end: today };                            // Tue–Fri → previous business day
}

function computePeriod(periodKey, customRange, reference = new Date()) {
  const today = _startOfDay(reference);
  const tomorrow = _addDays(today, 1);
  if (periodKey === "today") {
    return { start: today, end: tomorrow, label: _fmtDate(today, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) };
  }
  if (periodKey === "previous") {
    const { start, end } = previousOperationalPeriod(reference);
    const single = _addDays(start, 1).getTime() === end.getTime();
    const label = single
      ? _fmtDate(start, { weekday: "long", month: "long", day: "numeric" })
      : `${_fmtDate(start, { weekday: "long", month: "long", day: "numeric" })} – ${_fmtDate(_addDays(end, -1), { weekday: "long", month: "long", day: "numeric" })}`;
    return { start, end, label };
  }
  if (periodKey === "last7") {
    const start = _addDays(today, -6);
    return { start, end: tomorrow, label: `${_fmtDate(start, { month: "long", day: "numeric" })} – ${_fmtDate(today, { month: "long", day: "numeric", year: "numeric" })}` };
  }
  if (periodKey === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start, end: tomorrow, label: _fmtDate(start, { month: "long", year: "numeric" }) };
  }
  if (periodKey === "custom" && customRange?.from && customRange?.to) {
    const start = _startOfDay(new Date(customRange.from));
    const end = _addDays(_startOfDay(new Date(customRange.to)), 1);
    return { start, end, label: `${_fmtDate(start, { month: "long", day: "numeric", year: "numeric" })} – ${_fmtDate(_addDays(end, -1), { month: "long", day: "numeric", year: "numeric" })}` };
  }
  return { start: today, end: tomorrow, label: _fmtDate(today, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) };
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [viewOpen, setViewOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  // PO directive 2026-02-04 · Orders period selector wired to URL params
  // so "?period=month" or "?period=custom&from=2026-07-01&to=2026-07-05"
  // is a shareable link that lands the workspace in the same state.
  // Invalid ?period values silently fall back to "today".
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = useMemo(() => new Set(PERIOD_OPTIONS.map((o) => o.key)), []);
  const initialPeriod = validKeys.has(searchParams.get("period")) ? searchParams.get("period") : "today";
  const [period, setPeriod] = useState(initialPeriod);
  const [customRange, setCustomRange] = useState({
    from: searchParams.get("from") || "",
    to:   searchParams.get("to") || "",
  });

  // Reflect state changes back into the URL so refresh + share both work.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (period === "today") next.delete("period"); else next.set("period", period);
    if (period === "custom" && customRange.from && customRange.to) {
      next.set("from", customRange.from);
      next.set("to", customRange.to);
    } else {
      next.delete("from");
      next.delete("to");
    }
    // Replace so back-button doesn't fill with duplicate history entries.
    setSearchParams(next, { replace: true });
  }, [period, customRange.from, customRange.to]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders");
      setOrders(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const retrySend = async (orderId) => {
    setRetrying((prev) => ({ ...prev, [orderId]: true }));
    try {
      const res = await api.post(`/orders/${orderId}/resend-notification`);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? res.data : o)));
      if (res.data.notification_status === "Sent") {
        toast.success("Email notification delivered");
      } else {
        toast.error("Email send failed again");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const openView = async (orderId) => {
    setViewOpen(true);
    setViewOrder(null);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setViewOrder(res.data);
    } catch (e) {
      setViewOpen(false);
      toast.error(e?.response?.data?.detail || "Could not load order");
    }
  };

  const range = useMemo(() => computePeriod(period, customRange), [period, customRange]);
  // Business date the rack-price card follows. For any range that
  // ends today (today, last7, month) we show today's change. For a
  // custom or previous-period range, we show the LATEST day in the
  // range (range.end is exclusive → subtract one day using local
  // calendar arithmetic). Never uses UTC.
  const cardBusinessDate = useMemo(() => {
    const last = new Date(range.end);
    last.setDate(last.getDate() - 1);
    return _toBusinessDate(last);
  }, [range.end]);
  const inRange = orders.filter((o) => {
    const t = new Date(o.created_at).getTime();
    return t >= range.start.getTime() && t < range.end.getTime();
  });
  const q = query.trim().toLowerCase();
  const filtered = q
    ? inRange.filter(
        (o) =>
          o.order_number.toLowerCase().includes(q) ||
          o.customer_name.toLowerCase().includes(q) ||
          (o.driver_names || []).join(" ").toLowerCase().includes(q),
      )
    : inRange;

  return (
    <div data-testid="orders-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Orders</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <select
              data-testid="orders-period-selector"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            {period === "custom" && (
              <div className="flex items-center gap-2">
                <input
                  data-testid="orders-custom-from"
                  type="date"
                  value={customRange.from}
                  onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
                />
                <span className="text-sm text-gray-500">to</span>
                <input
                  data-testid="orders-custom-to"
                  type="date"
                  value={customRange.to}
                  onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
                />
              </div>
            )}
            <div className="text-sm text-gray-600">
              <span data-testid="orders-period-range" className="font-medium">{range.label}</span>
              <span className="ml-2 text-gray-400">·</span>
              <span data-testid="orders-period-count" className="ml-2">{inRange.length} order{inRange.length === 1 ? "" : "s"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 sm:flex-col-reverse sm:items-end lg:flex-row lg:items-start">
          <RackPriceChangeCard businessDate={cardBusinessDate} />
          <button
            data-testid="orders-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 shrink-0"
          >
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-5">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="todays-orders-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by order #, customer, or driver"
            className="h-11 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A] sm:w-96"
          />
        </div>
      </div>

      <div className="border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table data-testid="todays-orders-table" className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-[#f3f4f6] text-left text-sm text-gray-600">
                <th className="px-5 py-3 font-semibold">Time</th>
                <th className="px-5 py-3 font-semibold">Order #</th>
                <th className="px-5 py-3 font-semibold">Customer</th>
                <th className="px-5 py-3 font-semibold">Products</th>
                <th className="px-5 py-3 font-semibold">Assigned Driver</th>
                <th className="px-3 py-3 font-semibold" title="Employee responsible for the most recent workflow action">
                  <ActivityColumnHeader />
                </th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Notification</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-[15px] text-gray-500">
                    Loading orders…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-[15px] text-gray-500">
                    No orders found.
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`order-row-${o.order_number}`}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <td className="px-5 py-4 font-mono text-[15px] text-gray-700">
                    {new Date(o.created_at).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}
                  </td>
                  <td className="px-5 py-4 font-mono text-[15px]">
                    <button
                      data-testid={`view-order-${o.order_number}`}
                      onClick={() => openView(o.id)}
                      className="rounded-sm text-gray-700 underline-offset-2 hover:text-[#EB1D25] hover:underline focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
                    >
                      {o.order_number}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-[15px] font-medium text-gray-900">
                    <CustomerLink customerId={o.customer_id} customerName={o.customer_name} testId={`orderhistory-customer-${o.order_number}`} />
                  </td>
                  <td className="px-5 py-4 text-[15px] text-gray-700">
                    {o.products.map((p) => p.product_name).join(", ")}
                  </td>
                  <td className="px-5 py-4 text-[15px] text-gray-700">
                    {o.driver_names && o.driver_names.length > 0 ? o.driver_names.join(", ") : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-4">
                    <ActivityAvatar activity={getLastActivity(o)} testId={o.order_number} />
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-5 py-4">
                    <NotificationCell
                      order={o}
                      retrying={!!retrying[o.id]}
                      onRetry={() => retrySend(o.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <OrderViewDialog
        open={viewOpen}
        order={viewOrder}
        onClose={() => {
          setViewOpen(false);
          setViewOrder(null);
        }}
        onChanged={(updated) => {
          setViewOrder(updated);
          setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
        }}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Draft: "bg-gray-100 text-gray-700 border-gray-300",
    Sent: "bg-blue-50 text-blue-800 border-blue-300",
    Cancelled: "bg-red-50 text-red-800 border-red-300",
  };
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-sm font-medium ${styles[status] || styles.Sent}`}>
      {status}
    </span>
  );
}

/**
 * ActivityAvatar — EWO-ORD-004 (PO 2026-02-04)
 *
 * Compact circular initial-badge showing WHO performed the most recent
 * workflow action on this order. Hover tooltip reveals full activity,
 * employee name, and timestamp. Preserves horizontal table space vs
 * a full text column.
 *
 * "Latest activity" derivation (pure function over the Order document):
 *   Cancelled status                  → "Cancelled by"    at updated_at
 *   sent_at is set + revisions >= 1   → "Re-Sent by"      at sent_at
 *   sent_at is set                    → "Sent by"         at sent_at
 *   revisions >= 1                    → "Edited by"       at last revision.timestamp
 *   otherwise                         → "Created by"      at created_at
 *
 * V2 hook (deferred): make the avatar clickable → open Order Activity
 * History audit-trail modal (spec bullet).
 */
function NotificationCell({ order, retrying, onRetry }) {
  const s = order.notification_status || "Pending";
  if (s === "Sent") {
    return (
      <span
        data-testid={`notif-sent-${order.order_number}`}
        className="inline-flex items-center rounded-sm border border-green-300 bg-green-50 px-2 py-0.5 text-sm font-medium text-green-800"
      >
        Sent
      </span>
    );
  }
  if (s === "Failed") {
    return (
      <div className="flex items-center gap-2">
        <span
          data-testid={`notif-failed-${order.order_number}`}
          title={order.last_send_error || ""}
          className="inline-flex items-center gap-1 rounded-sm border border-red-300 bg-red-50 px-2 py-0.5 text-sm font-medium text-red-800"
        >
          <WarningCircle size={12} weight="bold" /> Failed
        </span>
        <button
          data-testid={`retry-send-${order.order_number}`}
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {retrying ? <Spinner size={11} className="animate-spin" /> : null}
          Retry Send
        </button>
      </div>
    );
  }
  return <span className="text-sm text-gray-500">Pending</span>;
}
